/**
 * ProviderWorkspace — workspace shown for provider-assigned workflow states.
 *
 * Layout:
 *   Mobile  : stacked — documents accordion → tab bar → tab content
 *   Desktop : two-column [documents sidebar | tabbed content area]
 *
 * Tabs: AI Summary | Expert Chat | Service Steps
 */
import * as React from 'react';
import {
  FileText,
  MessageCircle, Sparkles, ListChecks, ChevronDown, ChevronUp, PiggyBank,
} from 'lucide-react';
import { toast, AnimatedTabs, type AnimatedTabItem } from '@finmin/ui';
import { formatCurrency, zipAndDownload, type ZipEntry } from '@finmin/utils';
import type { CustomerServiceRequestResponse } from '@/api/service-requests';
import { useDocumentsForRequest, useUploadDocument, useDownloadDocument } from '@/api/documents';
import { paymentGateMessage, usePaymentStatus } from '@/api/payments';
import { parseAiResponse, normaliseRequiredDocs, toUploadTarget, docFillsSlot, validateDocumentFile, getDocLabel, triggerBlobDownload, formatFeeRange, formatTat } from '../utils';
import { INTERNAL_DOC_TYPES } from '../constants';
import { PALETTE_FOREST } from '../tokens';
import { AiSummaryPanel } from './ai-summary';
import { ChatBox } from './chat-box';
import { ServiceStepsTab } from './service-steps-tab';
import { WorkspaceDocumentsPanel } from './workspace-documents-panel';
import { ServiceEstimateCard } from './service-estimate-card';
import { ServiceRatingModal } from './service-rating-card';
import { UnmaskControl } from '@/features/unmask';
import { PaymentSection } from './payment-modal';
import { ServiceRequestBills } from '@/features/billing/bills-list';

// ── Types ─────────────────────────────────────────────────────────────────────

type Tab = 'summary' | 'steps';

interface Props {
  request:   CustomerServiceRequestResponse;
  onRefresh: () => void;
  /** Service Closure: uploads are rejected (409) while on hold / closed. */
  readOnly?: boolean;
}

// ── Money-saved highlight ──────────────────────────────────────────────────────

/** Celebratory "we saved you ₹X" banner — shown only once a completed request
 *  has an admin-recorded money_saved value. */
function MoneySavedHighlight({
  amount,
  className,
}: {
  amount: string;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-border p-4 ${className ?? ''}`}
      style={{ backgroundColor: 'hsl(var(--palette-butter, 43 100% 91%))' }}
    >
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide" style={{ color: PALETTE_FOREST }}>
        <PiggyBank className="h-4 w-4" aria-hidden /> Money saved
      </div>
      <p className="mt-1 text-2xl font-bold tracking-tight" style={{ color: PALETTE_FOREST }}>
        {formatCurrency(amount)}
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">on this service request</p>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ProviderWorkspace({ request, readOnly = false }: Props) {
  const [activeTab,   setActiveTab]   = React.useState<Tab>('summary');
  const [docsOpen,    setDocsOpen]    = React.useState(false);
  const [uploadingId, setUploadingId] = React.useState<string | null>(null);
  const [zipping,     setZipping]     = React.useState(false);

  const isCompleted = request.customer_workflow_state === 'service_request_complete';
  const showSaved = isCompleted && request.money_saved != null;
  // The unpaid "Pay now" gate now lives in a route-level blocking overlay
  // (<PaymentGateOverlay>) so it surfaces on ANY stage, not just here. This
  // inline surface only renders the post-payment receipt once captured (the
  // gate lifts on capture, so `payment_required` flips false — we key off the
  // captured payment status instead). See CLAUDE.md → "Payments & rewards".
  const { data: paymentStatus } = usePaymentStatus(request.id, { retry: false });
  const isPaid = paymentStatus?.status === 'captured';
  // Chat is a dedicated right-side rail. The composer inside ChatBox decides
  // whether sending is allowed (gated until the provider is assigned); the
  // transcript is always readable.


  const { data: uploadedDocs = [], refetch: refetchDocs }                             = useDocumentsForRequest(request.id);
  const { mutateAsync: uploadFile }                                                    = useUploadDocument();
  const { mutateAsync: downloadDoc, isPending: isDownloading, variables: dlVars }  = useDownloadDocument();

  const parsedAi = React.useMemo(
    () => parseAiResponse(request.ai_agent1_response),
    [request.ai_agent1_response],
  );

  const requiredDocs = React.useMemo(
    () => normaliseRequiredDocs(
      request.required_documents_from_customer_predicted_by_agent1 ?? request.merged_required_documents,
      INTERNAL_DOC_TYPES,
    ),
    [request.required_documents_from_customer_predicted_by_agent1, request.merged_required_documents],
  );

  const missingDocTypes = React.useMemo(
    () => requiredDocs
      .filter(d => !uploadedDocs.some(u => docFillsSlot(u, d)))
      .map(d => d.document_type),
    [requiredDocs, uploadedDocs],
  );

  const feeDisplay = React.useMemo(
    () => formatFeeRange(request.customer_service_fee_min, request.customer_service_fee_max),
    [request.customer_service_fee_min, request.customer_service_fee_max],
  );

  const tatDisplay = React.useMemo(
    () => formatTat(request.expected_tat, request.tat_min, request.tat_max),
    [request.expected_tat, request.tat_min, request.tat_max],
  );

  const hasEstimate = Boolean(feeDisplay || tatDisplay);

  async function handleDownload(documentId: string) {
    try {
      const { blob, filename } = await downloadDoc({ serviceRequestId: request.id, documentId });
      triggerBlobDownload(blob, filename);
    } catch {
      toast.error('Could not download the document. Please try again.');
    }
  }

  async function handleUpload(file: File, documentType = 'other', documentLabel?: string): Promise<boolean> {
    if (readOnly) {
      toast.error('Uploads are paused while this service is on hold or closed.');
      return false;
    }
    const err = validateDocumentFile(file);
    if (err) { toast.error(err); return false; }
    const slotId = `${documentType}-${Date.now()}`;
    setUploadingId(slotId);
    try {
      const slot   = requiredDocs.find(d => d.document_type === documentType);
      const target = slot ? toUploadTarget(slot.document_type, slot.label, documentLabel) : null;
      await uploadFile({
        serviceRequestId: request.id,
        file,
        documentType:  target?.documentType  ?? documentType,
        documentLabel: target?.documentLabel ?? documentLabel,
      });
      toast.success(`${documentLabel?.trim() || slot?.label || getDocLabel(documentType)} uploaded successfully.`);
      await refetchDocs();
      return true;
    } catch (err) {
      toast.error(paymentGateMessage(err) ?? 'Upload failed. Please try again.');
      return false;
    } finally {
      setUploadingId(null);
    }
  }

  const tabs: AnimatedTabItem<Tab>[] = [
    { value: 'summary', label: 'Special Insights', icon: Sparkles },
    { value: 'steps',   label: 'Service Steps', icon: ListChecks },
  ];

  async function handleDownloadAll() {
    const list = uploadedDocs.filter((d) => d.is_downloadable);
    if (list.length === 0 || zipping) return;
    setZipping(true);
    try {
      const entries: ZipEntry[] = [];
      for (const doc of list) {
        const { blob, filename } = await downloadDoc({ serviceRequestId: request.id, documentId: doc.id });
        entries.push({ name: filename, blob });
      }
      await zipAndDownload(entries, `documents-${request.id.slice(0, 8)}`);
    } catch {
      toast.error('Could not download all documents. Please try again.');
    } finally {
      setZipping(false);
    }
  }

  const documentsPanel = (
    <WorkspaceDocumentsPanel
      uploadedDocs={uploadedDocs}
      missingDocTypes={missingDocTypes}
      isDownloading={isDownloading}
      downloadingDocId={dlVars?.documentId}
      uploadingId={uploadingId}
      onDownload={handleDownload}
      onUpload={handleUpload}
      onDownloadAll={handleDownloadAll}
      isZipping={zipping}
      readOnly={readOnly}
    />
  );

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-4 pb-8">

      {/* Mobile / tablet: documents accordion (desktop uses the sticky sidebar) */}
      <div className="xl:hidden mb-4 rounded-2xl border border-border bg-background overflow-hidden">
        <button
          type="button"
          onClick={() => setDocsOpen(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3"
        >
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4" style={{ color: PALETTE_FOREST }} />
            <span className="text-sm font-semibold text-foreground">Documents</span>
            {uploadedDocs.length > 0 && (
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: `${PALETTE_FOREST}15`, color: PALETTE_FOREST }}
              >
                {uploadedDocs.length}
              </span>
            )}
            {missingDocTypes.length > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400">
                {missingDocTypes.length} pending
              </span>
            )}
          </div>
          {docsOpen
            ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
            : <ChevronDown className="h-4 w-4 text-muted-foreground" />
          }
        </button>
        {docsOpen && (
          <div className="border-t border-border px-4 pb-4 pt-3">
            {documentsPanel}
          </div>
        )}
      </div>

      {/* Mobile / tablet: money saved (when completed), fee + expected time, then the reveal action. */}
      {showSaved && <MoneySavedHighlight amount={request.money_saved!} className="xl:hidden mb-4" />}
      {hasEstimate && (
        <ServiceEstimateCard
          feeDisplay={feeDisplay}
          tatDisplay={tatDisplay}
          className="xl:hidden mb-4"
        />
      )}
      {/* This request's bills (invoices, receipts, refunds). Renders nothing until one exists. */}
      <ServiceRequestBills serviceRequestId={request.id} className="xl:hidden mb-4" />
      <UnmaskControl compact className="xl:hidden mb-4 w-full justify-center" />

      {/* Main grid — documents sidebar | tabbed content | chat rail (3-col on xl) */}
      <div className="grid grid-cols-1 gap-4 lg:gap-6 xl:grid-cols-[300px_minmax(0,1fr)_360px]">

        {/* Desktop: sticky sidebar — documents, fee + expected time, then reveal action. */}
        <div className="hidden xl:block">
          <div className="sticky top-[8rem] space-y-4">
            {showSaved && <MoneySavedHighlight amount={request.money_saved!} />}
            <div className="rounded-2xl border border-border bg-background p-4">
              {documentsPanel}
            </div>
            {hasEstimate && (
              <ServiceEstimateCard
                feeDisplay={feeDisplay}
                tatDisplay={tatDisplay}
              />
            )}
            <ServiceRequestBills serviceRequestId={request.id} />
            <UnmaskControl compact className="w-full justify-center" />
          </div>
        </div>

        {/* Content area — fixed viewport height on desktop so inner panels scroll, not the page. */}
        <div className="rounded-2xl border border-border bg-background flex flex-col overflow-hidden h-[calc(100vh-16rem)] xl:h-[calc(100vh-15rem)] shadow-sm">
          {/* Completion rating — auto-opening modal the moment a completed,
              unrated request opens. Portals out of this layout; dismissible. */}
          {isCompleted && <ServiceRatingModal request={request} />}

          {/* Post-payment receipt. The unpaid "Pay now" gate is handled by the
              route-level <PaymentGateOverlay>; here we only show the captured
              fee/reward breakdown once paid. */}
          {isPaid && <PaymentSection request={request} />}

          {/* Tab bar with animated underline + hidden scrollbar */}
          <div className="shrink-0">
            <AnimatedTabs
              items={tabs}
              value={activeTab}
              onChange={setActiveTab}
              accent="forest"
              size="sm"
              ariaLabel="Service workspace tabs"
            />
          </div>

          {/* Tab content — min-h-0 lets flex children control their own overflow.
              Keyed so each tab switch replays a brief slide-up-fade. */}
          <div key={activeTab} className="flex-1 flex flex-col min-h-0 animate-slide-up-fade">
            {activeTab === 'summary' && (
              <div className="flex-1 overflow-y-auto p-5 sm:p-6">
                {parsedAi.sections.length > 0 ? (
                  <AiSummaryPanel sections={parsedAi.sections} />
                ) : (
                  <div className="flex flex-col items-center gap-3 py-12 text-center">
                    <Sparkles className="h-8 w-8" style={{ color: PALETTE_FOREST, opacity: 0.4 }} />
                    <p className="text-sm text-muted-foreground">AI analysis is being prepared…</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'steps' && (
              <div className="flex-1 overflow-y-auto p-5 sm:p-6">
                <ServiceStepsTab request={request} />
              </div>
            )}
          </div>
        </div>

        {/* Chat rail — dedicated conversation column, like the admin & provider workspaces.
            ChatBox owns its own scroll; the composer gates sending until a provider is assigned. */}
        <div className="rounded-2xl border border-border bg-background flex flex-col overflow-hidden h-[70vh] xl:h-[calc(100vh-15rem)] xl:sticky xl:top-[8rem] shadow-sm">
          <div className="shrink-0 flex items-center gap-2 border-b border-border px-4 py-3">
            <MessageCircle className="h-4 w-4" style={{ color: PALETTE_FOREST }} aria-hidden />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground tracking-tight">Chat</p>
              <p className="text-[11px] text-muted-foreground truncate">Message your expert &amp; the FinminOS team</p>
            </div>
          </div>
          <div className="flex-1 flex flex-col min-h-0">
            <ChatBox request={request} />
          </div>
        </div>
      </div>
    </div>
  );
}
